import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SignupForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  
  const [selectedPlan, setSelectedPlan] = useState(planParam || 'lets-be-pals');
  const isSubscription = ['your-pal', 'my-pal', 'best-pals'].includes(selectedPlan);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const plans = [
    { id: 'lets-be-pals', name: "Let's Be Pals", price: '$19.99', period: 'per pickup', features: ['1 pickup', 'Up to 3 packages', 'No commitment'] },
    { id: 'your-pal', name: 'Your Pal', price: '$39.99', period: '/month', features: ['2 pickups/month', '10 packages total', 'Rollover packages'], badge: '⭐ Popular' },
    { id: 'my-pal', name: 'My Pal', price: '$49.99', period: '/month', features: ['3 pickups/month', '15 packages total', 'Priority support'], badge: '🏆 Best Value' },
    { id: 'best-pals', name: 'Best Pals', price: '$59.99', period: '/month', features: ['4 pickups/month', '20 packages total', 'VIP service'] }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      agreeToTerms: checked
    }));
    if (errors.agreeToTerms) {
      setErrors(prev => ({ ...prev, agreeToTerms: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }
    
    // Password required for ALL accounts so customers can log back in securely.
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Save user to backend
        const response = await fetch(`${API_URL}/api/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phoneNumber,
            password: formData.password,
            is_subscription: isSubscription,
            plan_type: selectedPlan
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to save user information');
        }
        
        // Store form data in sessionStorage
        const customerInfo = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          planType: selectedPlan,
          isSubscription: isSubscription
        };
        
        sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));
        
        // Navigate directly to schedule pickup for ALL users
        // Payment (including first subscription payment) happens at the END
        navigate('/schedule-pickup');
      } catch (error) {
        setErrors({ submit: 'Failed to save your information. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="signup-page">
      <nav className="nav-header">
        <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png" 
            alt="ReturnPal" 
            className="logo-image" style={{ height: "50px", width: "auto" }}
          />
        </div>
      </nav>

      <div className="signup-container">
        <div className="signup-content">
          <h1 className="signup-title">Step 1: Choose Your Plan</h1>
          <p className="signup-subtitle">Select the plan that works best for you</p>
          
          {/* Plan Selection Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                style={{
                  position: 'relative',
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: selectedPlan === plan.id ? '3px solid #10b981' : '2px solid #e5e7eb',
                  backgroundColor: selectedPlan === plan.id ? '#f0fdf4' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {plan.badge && (
                  <div style={{ position: 'absolute', top: '-10px', right: '10px', backgroundColor: '#f59e0b', color: 'white', padding: '3px 10px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '600' }}>
                    {plan.badge}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '700', color: selectedPlan === plan.id ? '#059669' : '#1f2937', margin: 0 }}>{plan.name}</h3>
                  {selectedPlan === plan.id && <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.8rem' }}>✓</div>}
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937' }}>{plan.price}</span>
                  <span style={{ fontSize: '0.85rem', color: '#6b7280', marginLeft: '4px' }}>{plan.period}</span>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.8rem', color: '#4b5563' }}>
                  {plan.features.map((feature, idx) => (
                    <li key={`${plan.id}-feature-${idx}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <span style={{ color: '#10b981', marginRight: '6px' }}>✓</span>{feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h2 className="signup-title" style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>Step 2: Enter Your Details</h2>
          <p className="signup-subtitle" style={{ marginBottom: '1.5rem' }}>
            {isSubscription ? `You selected ${plans.find(p => p.id === selectedPlan)?.name}. You'll pay for your subscription + add-ons at checkout.` : 'Pay only when you book - no subscription.'}
          </p>
          
          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={errors.firstName ? 'input-error' : ''}
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>

              <div className="form-group">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={errors.lastName ? 'input-error' : ''}
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={errors.phoneNumber ? 'input-error' : ''}
              />
              {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email (Your Username)</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'input-error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            {/* Password fields — required for all accounts */}
            <div className="form-group">
              <Label htmlFor="password">Create Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Minimum 8 characters"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'input-error' : ''}
                data-testid="signup-password"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'input-error' : ''}
                data-testid="signup-confirm-password"
              />
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <div className="form-group-checkbox">
              <div className="checkbox-wrapper">
                <Checkbox
                  id="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onCheckedChange={handleCheckboxChange}
                />
                <Label htmlFor="agreeToTerms" className="checkbox-label">
                  I agree to the{' '}
                  <span onClick={() => window.open('/terms', '_blank')} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                    Terms of Service
                  </span>
                  {' and '}
                  <span onClick={() => window.open('/privacy', '_blank')} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                    Privacy Policy
                  </span>
                </Label>
              </div>
              {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
            </div>

            {errors.submit && (
              <div className="error-message" style={{ marginBottom: '1rem', color: '#c00' }}>
                {errors.submit}
              </div>
            )}

            <Button type="submit" className="btn-primary btn-large w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Schedule a Pick Up'}
            </Button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignupForm;
